import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAdminContent, AdminScript } from "@/hooks/useAdminContent";
import { ContentStats } from "@/components/admin/content/ContentStats";
import { ContentFilters } from "@/components/admin/content/ContentFilters";
import { ContentTable } from "@/components/admin/content/ContentTable";
import { ContentDetailModal } from "@/components/admin/content/ContentDetailModal";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminContent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    scripts,
    stats,
    loading,
    totalCount,
    fetchScripts,
    fetchStats,
    updateScriptStatus,
    deleteScript,
  } = useAdminContent();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sceneType, setSceneType] = useState("all");
  const [status, setStatus] = useState("all");
  const pageSize = 20;

  // Modal states
  const [selectedScript, setSelectedScript] = useState<AdminScript | null>(null);
  const [hideConfirmScript, setHideConfirmScript] = useState<AdminScript | null>(null);
  const [deleteConfirmScript, setDeleteConfirmScript] = useState<AdminScript | null>(null);

  // Fetch data
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchScripts({ page, pageSize, search: debouncedSearch, sceneType, status });
  }, [page, pageSize, debouncedSearch, sceneType, status, fetchScripts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, pageSize, sceneType, status]);

  const handleView = (script: AdminScript) => {
    setSelectedScript(script);
  };

  const handleHide = (script: AdminScript) => {
    setHideConfirmScript(script);
  };

  const handleDelete = (script: AdminScript) => {
    setDeleteConfirmScript(script);
  };

  const confirmHide = async () => {
    if (!hideConfirmScript) return;
    const newStatus = hideConfirmScript.status === 'hidden' ? 'active' : 'hidden';
    const success = await updateScriptStatus(hideConfirmScript.id, newStatus);
    if (success) {
      toast({
        title: newStatus === 'hidden' ? t('admin.content.toasts.hiddenTitle') : t('admin.content.toasts.restoredTitle'),
        description: t('admin.content.toasts.statusUpdated', { title: hideConfirmScript.title }),
      });
    }
    setHideConfirmScript(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmScript) return;
    const success = await deleteScript(deleteConfirmScript.id, deleteConfirmScript.title);
    if (success) {
      toast({

        title: t('admin.content.toasts.deletedTitle'),
        description: t('admin.content.toasts.deletedDesc', { title: deleteConfirmScript.title }),
        variant: 'destructive',
      });
    }
    setDeleteConfirmScript(null);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8 space-y-1">
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.content.title')}</h1>
        <p className="text-lg font-medium text-gray-500">{t('admin.content.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <ContentStats
        total={stats.total}
        todayNew={stats.todayNew}
        hidden={stats.hidden}
        activeRate={stats.activeRate}
        loading={loading}
      />

      {/* Filters */}
      <ContentFilters
        search={search}
        onSearchChange={setSearch}
        sceneType={sceneType}
        onSceneTypeChange={(v) => { setSceneType(v); setPage(1); }}
        status={status}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
      />

      {/* Table */}
      <ContentTable
        scripts={scripts}
        loading={loading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onView={handleView}
        onHide={handleHide}
        onDelete={handleDelete}
      />

      {/* Detail Modal */}
      <ContentDetailModal
        script={selectedScript}
        onClose={() => setSelectedScript(null)}
      />

      {/* Hide Confirm Dialog */}
      <AlertDialog open={!!hideConfirmScript} onOpenChange={() => setHideConfirmScript(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hideConfirmScript?.status === 'hidden' ? t('admin.content.dialogs.restoreTitle') : t('admin.content.dialogs.hideTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hideConfirmScript?.status === 'hidden'
                ? t('admin.content.dialogs.restoreConfirm', { title: hideConfirmScript?.title })
                : t('admin.content.dialogs.hideConfirm', { title: hideConfirmScript?.title })
              }
              <br />
              {hideConfirmScript?.status === 'hidden'
                ? t('admin.content.dialogs.restoreDesc')
                : t('admin.content.dialogs.hideDesc')
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.settings.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmHide}>
              {hideConfirmScript?.status === 'hidden' ? t('admin.content.dialogs.confirmRestore') : t('admin.content.dialogs.confirmHide')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirm Dialog */}
      <AlertDialog open={!!deleteConfirmScript} onOpenChange={() => setDeleteConfirmScript(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.content.dialogs.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.content.dialogs.deleteConfirm', { title: deleteConfirmScript?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.settings.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('admin.content.dialogs.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
